import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';

const services = [
  {
    icon: 'fa-solid fa-cloud-arrow-up',
    title: '1. Azure Infrastructure Migration & Modernization',
    desc: 'Seamlessly migrate legacy workloads, virtual machines, and databases to Microsoft Azure using automated cloud adoption frameworks to ensure high availability and scalability.',
  },
  {
    icon: 'fa-solid fa-brain',
    title: '2. Azure OpenAI & Cognitive AI Services',
    desc: 'Deploy custom generative AI models, enterprise search engines (RAG), and intelligent computer vision pipelines integrated securely with your corporate Azure tenant.',
  },
  {
    icon: 'fa-solid fa-square-poll-vertical',
    title: '3. Power Platform Development & Automation',
    desc: 'Build custom low-code enterprise applications with Power Apps, automate complex multi-app workflows with Power Automate, and visualize insights via Power BI.',
  },
  {
    icon: 'fa-solid fa-arrows-split-up-and-left',
    title: '4. Azure Integration Services & Microservices',
    desc: 'Architect resilient cloud-native apps using Azure Kubernetes Service (AKS), Azure Functions (Serverless), Azure Logic Apps, and Service Bus integration channels.',
  },
  {
    icon: 'fa-solid fa-shield-halved',
    title: '5. Microsoft Defender & Sentinel Security',
    desc: 'Implement zero-trust security postures with Microsoft Defender, automated threat detection, and cloud-native SIEM/SOAR monitoring via Azure Sentinel.',
  },
  {
    icon: 'fa-solid fa-users-gear',
    title: '6. Microsoft 365 & Digital Workplace',
    desc: 'Deploy secure hybrid workplace solutions with Microsoft Teams, SharePoint Online, OneDrive, and Microsoft Intune for mobile device management.',
  },
  {
    icon: 'fa-solid fa-database',
    title: '7. Azure Synapse & Fabric Data Lakehouses',
    desc: 'Unify big data analytics, petabyte-scale data lakes, and automated ETL pipelines using Azure Synapse Analytics and Microsoft Fabric.',
  },
  {
    icon: 'fa-solid fa-key',
    title: '8. Microsoft Entra ID (Azure AD) & Identity',
    desc: 'Enforce conditional access policies, multi-factor authentication (MFA), privileged identity management (PIM), and single sign-on (SSO) across enterprise applications.',
  },
  {
    icon: 'fa-solid fa-rotate',
    title: '9. Azure DevOps & Infrastructure as Code (IaC)',
    desc: 'Accelerate software delivery through Azure DevOps CI/CD pipelines, Bicep/Terraform infrastructure provisioning, and continuous automated testing.',
  },
  {
    icon: 'fa-solid fa-chart-line-down',
    title: '10. Azure FinOps & Cost Optimization',
    desc: 'Audit cloud resource utilization, enforce Azure Policy guardrails, eliminate idle compute capacity, and leverage reserved instances to reduce cloud spend.',
  },
];

const whyChooseUs = [
  'Certified Microsoft Azure Solutions Architects and Cloud Engineers',
  'End-to-end expertise across Azure, Power Platform, and M365 ecosystems',
  'Deep specialization in Azure OpenAI and enterprise Copilot rollouts',
  'Zero-Trust security implementation with Microsoft Sentinel and Entra ID',
  'Proven Microsoft Cloud Adoption Framework (CAF) migration blueprints',
  'Cloud-native microservices development with Azure Kubernetes (AKS)',
  'Proactive FinOps methodologies for continuous cost management',
  'Seamless hybrid cloud connectivity with Azure Arc and ExpressRoute',
  'Accelerated Power Automate and low-code digital transformations',
  '24/7 proactive cloud infrastructure management and monitoring support',
];

const processSteps = [
  { step: '01', title: 'Assessment & Strategy', desc: 'Audit current infrastructure, licensing models, security posture, and define your Microsoft Cloud roadmap.' },
  { step: '02', title: 'Architecture Blueprinting', desc: 'Design landing zones, network security boundaries, identity management, and cloud architecture.' },
  { step: '03', title: 'Migration & Application Development', desc: 'Execute lift-and-shift or re-platforming sequences while building modern Azure microservices.' },
  { step: '04', title: 'Security & Governance Rollout', desc: 'Apply Microsoft Sentinel SIEM, Entra ID zero-trust policies, and automated compliance frameworks.' },
  { step: '05', title: 'Validation & User Enablement', desc: 'Conduct performance stress testing, migration verification, and hands-on M365/Power Apps training.' },
  { step: '06', title: 'Continuous FinOps & Operations', desc: 'Optimize resource utilization, manage cloud spend, and provide ongoing 24/7 Azure support.' },
];

const techStack = [
  { category: 'Compute & Cloud Infrastructure', items: ['Azure VMs', 'Azure Kubernetes (AKS)', 'Azure App Service', 'Azure Functions', 'Azure Arc'] },
  { category: 'AI & Data Services', items: ['Azure OpenAI', 'Microsoft Fabric', 'Azure Synapse', 'Azure SQL DB', 'Cosmos DB'] },
  { category: 'Security & Identity', items: ['Microsoft Entra ID', 'Microsoft Sentinel', 'Defender for Cloud', 'Intune', 'Key Vault'] },
  { category: 'Productivity & DevOps', items: ['Microsoft 365', 'Power Platform', 'Azure DevOps', 'Terraform', 'Bicep'] },
];

// Animation Variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.12 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
};

const MicrosoftCloudSolutions = () => {
  return (
    <div className="service-page text-white overflow-hidden">
      {/* 1. HERO SECTION */}
      <section className="hero-sub-section text-center d-flex align-items-center justify-content-center">
        <div className="container py-5">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="badge rounded-pill bg-outline-cyan px-3 py-2 mb-3 glow-badge">
              Lidlbritain Technology Solutions
            </span>
            <h1 className="display-4 fw-bold mb-3">
              Microsoft Cloud <span className="gradient-text-cyan">Solutions</span>
            </h1>
            <p className="lead mx-auto opacity-75 max-width-800">
              At <strong>Lidlbritain</strong>, we empower organizations to harness the full strength of the Microsoft ecosystem. From Azure cloud migrations and AI innovation to Power Platform automation and zero-trust security, we engineer scalable digital transformations.
            </p>
            <div className="mt-4">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
                <Link to="/contact" className="btn gradient-btn px-4 py-2 rounded-pill">
                  Scale Your Microsoft Cloud &rarr;
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. SERVICES GRID SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              Microsoft Cloud Solutions by <span className="gradient-text-orange">Lidlbritain</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
          >
            {services.map((item, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ y: -8, transition: { duration: 0.2 } }}
                  className="service-card p-4 rounded-4 h-100 border border-secondary border-opacity-25 bg-glass"
                >
                  <div className="icon-box mb-3 d-flex align-items-center justify-content-center floating-icon">
                    <i className={`${item.icon} fs-3 text-cyan`}></i>
                  </div>
                  <h5 className="fw-bold mb-3">{item.title}</h5>
                  <p className="text-light opacity-75 small leading-relaxed">{item.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 3. WHY CHOOSE US & TECH STACK SECTION */}
      <section className="py-5 bg-dark-overlay">
        <div className="container">
          <div className="row g-5 align-items-center">
            {/* Why Choose Lidlbritain */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: -40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h3 className="fw-bold mb-4">
                Why Partner With <span className="gradient-text-cyan">Lidlbritain?</span>
              </h3>
              <div className="row g-3">
                {whyChooseUs.map((reason, idx) => (
                  <div key={idx} className="col-sm-6">
                    <div className="d-flex align-items-center gap-2">
                      <i className="fa-solid fa-circle-check text-orange pulse-icon"></i>
                      <span className="small opacity-90">{reason}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Tools & Tech Stack */}
            <motion.div
              className="col-lg-6"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="p-4 rounded-4 border border-secondary border-opacity-25 bg-glass glow-box">
                <h4 className="fw-bold mb-4">Microsoft Cloud Tech Ecosystem</h4>
                {techStack.map((tech, idx) => (
                  <div key={idx} className="mb-3">
                    <h6 className="text-cyan mb-2 fw-semibold">{tech.category}</h6>
                    <div className="d-flex flex-wrap gap-2">
                      {tech.items.map((item, i) => (
                        <motion.span
                          whileHover={{ scale: 1.1 }}
                          key={i}
                          className="badge bg-secondary bg-opacity-22 border border-secondary border-opacity-50 text-light fw-normal py-2 px-3"
                        >
                          {item}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. PROCESS SECTION */}
      <section className="py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fw-bold">
              The Lidlbritain <span className="gradient-text-orange">Microsoft Cloud Framework</span>
            </h2>
            <div className="title-line mx-auto mt-2"></div>
          </div>

          <motion.div
            className="row g-4"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {processSteps.map((stepItem, index) => (
              <div key={index} className="col-md-6 col-lg-4">
                <motion.div
                  variants={cardVariants}
                  whileHover={{ scale: 1.03 }}
                  className="process-card p-4 rounded-4 h-100 bg-glass border border-secondary border-opacity-25 position-relative"
                >
                  <span className="step-number fw-bold text-cyan mb-2 d-block">{stepItem.step}</span>
                  <h5 className="fw-bold mb-2">{stepItem.title}</h5>
                  <p className="text-light opacity-75 small mb-0">{stepItem.desc}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 5. CALL TO ACTION SECTION */}
      <section className="py-5 my-4">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="p-5 rounded-5 cta-banner text-center position-relative overflow-hidden"
          >
            <h2 className="fw-bold mb-3">Maximize Your Microsoft Investment with Lidlbritain</h2>
            <p className="lead opacity-90 mx-auto mb-4 max-width-800">
              Modernize application workloads, build enterprise AI pipelines, and safeguard your digital environment with proven Azure strategies.
            </p>
            <motion.div whileHover={{ scale: 1.08 }} whileTap={{ scale: 0.95 }} className="d-inline-block">
              <Link to="/contact" className="btn gradient-btn btn-lg px-5 py-3 rounded-pill fw-semibold">
                Consult with a Microsoft Cloud Architect &rarr;
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default MicrosoftCloudSolutions;